import  { Resource } from "./Resource";
import  { LogGroup , Log ,MyPIXIContainer } from ".././MyHelpers";
import  { ResourceIdentifier } from "./ResourceIdentifier";
import { LoadResources } from "../system/CommonStartup";
import { max } from "date-fns";

export enum LoadingStage
{
	Idle = "Idle",
	System = "System",
	FillingPotFeature = "FillingPotFeature",
	GamesConfig = "GamesConfig",
	UniReel = "UniReel",
	GamesPreload = "GamesPreload",
	Games = "Games",
	Finished = "Finished"
}


export const LoadingStageOrder: LoadingStage[] = [
	LoadingStage.System,
	LoadingStage.FillingPotFeature,
	LoadingStage.GamesConfig,
	LoadingStage.UniReel,
	LoadingStage.GamesPreload,
	LoadingStage.Games,
	LoadingStage.Finished,	
];


export type ReadyToLoadCallback = () => void;
export type LoadStartCallback = (resources: Resource[]) => void;
export type LoadProgressCallback = (percent: number) => void;
export type LoadFinishedCallback = () => void;
export type StageLoadFinishedCallback = (stage: LoadingStage) => void;


export interface IWriteOnlyResourceManager
{
	EnableLogging();
	DisableLogging();

	WithLogging(cb: () => void): void;
	SetCurrentGame(gameName: string): void;
	Add<T extends Resource>(resource: T): void;
	AddGlobal<T extends Resource>(resource: T) : T;
}

export interface IReadOnlyResourceManager
{
	EnableLogging();
	DisableLogging();

	WithLogging(cb: () => void): void;

	Get<T extends Resource>(resourceName: string | ResourceIdentifier): T;
	SetCurrentGame(gameName: string): void;
}


export class ResourceManager implements IReadOnlyResourceManager
{
	private resources: Map<string, Resource> = new Map<string, Resource>();

	//private resourcesProcessed: number = 0;
	private resourcesLoaded: number = 0;
	private resourcesLoadError: number = 0;

	protected logGroup: LogGroup = null;

	private loadTimeout: number = 10 * 1000;
	private loadTimeoutTimer: number = 0;

	private preventCache: boolean = true;

	private loadingInProgress: boolean = false;

	private allowPostFinishResourceAdd: boolean = false;
	private onDemandLoadActive: boolean = false;

	public EnablePostFinishResourceAdd(enable: boolean = true): void
	{
		this.allowPostFinishResourceAdd = enable;
		this.loadingInProgress = false;
	}

	public async LoadUnprocessedResources(): Promise<void>
	{
		const unprocessed = this.UnprocessedResources();
		if (unprocessed.length == 0)
		{
			return;
		}

		this.onDemandLoadActive = true;
		this.loadingInProgress = true;
		this.currentBatchIndex = 0;
		this.currentBatchProcessed = 0;

		this.loadPromise = new Promise<void>((resolve, reject) => {
			this.loadPromiseResolve = resolve;
			this.loadPromiseReject = reject;
		});

		setTimeout(() => this.LoadNextBatch(), 10);
		await this.loadPromise;

		this.onDemandLoadActive = false;
		this.loadingInProgress = false;
	}

	protected failedResources: Resource[] = [];

	protected paths: string[] = [];


	
	public constructor(logGroup: LogGroup)
	{
		this.logGroup = logGroup.Add("ResourceManager");
	}
	
	protected loadingStage: LoadingStage = LoadingStage.Idle;
	public get LoadingStage(): LoadingStage
	{
		return this.loadingStage;
	}

	private resourcesByStage: Map<LoadingStage, Map<string, Resource>> = new Map<LoadingStage, Map<string, Resource>>();
	public SetLoadingStage(newLoadingStage: LoadingStage)
	{
		let currentLoadingStageIndex = LoadingStageOrder.indexOf(this.loadingStage);
		let newLoadingStageIndex = LoadingStageOrder.indexOf(newLoadingStage);

		if (newLoadingStageIndex <= currentLoadingStageIndex)
		{
			throw `Can't set loading stage to ${newLoadingStage}, already in ${this.loadingStage}`;
		}

		this.loadingInProgress = false;
		this.loadingStage = newLoadingStage;
		this.resourcesByStage.set(this.loadingStage, new Map<string, Resource>());
	}


	protected currentGame: string = null;
	protected resourcesByGame: Map<string, Resource[]> = new Map<string, Resource[]>();
	public SetCurrentGame(gameName: string)
	{
		if (!this.resourcesByGame.has(gameName))
		{
			this.resourcesByGame.set(gameName, []);
			// throw `Can't set current game to ${gameName}, already set`;
		}

		this.currentGame = gameName;
	}


	protected logging: boolean = false;
	public EnableLogging() {
		this.logging = true;
	}

	public DisableLogging() {
		this.logging = false;
	}

	public WithLogging(cb: () => void) {
		this.logging = true;
		try
		{
			cb();
		}
		catch {}
		this.logging = false;
	}

	public Add<T extends Resource>(resource: T): void
	{

		let resourcesThisStage = this.resourcesByStage.get(this.loadingStage);

		//if (this.logging)
		{
			console.log(`    ResourceManager stage ${this.loadingStage} Adding resource ${resourcesThisStage.size +1} type ${resource.Type} name ${resource.name} from ${resource.path}`);
		}

		if (this.loadingStage == LoadingStage.Idle && !this.allowPostFinishResourceAdd)
		{
			throw "ResourceManager: No loading stage set, can't add resource";
		}

		if ((this.loadingStage == LoadingStage.Games || this.allowPostFinishResourceAdd) && !this.currentGame)
		{
			throw "ResourceManager: No current game set, can't add resource";
		}

		if (this.loadingInProgress && !this.onDemandLoadActive) {
			throw "Can't add resources after already started loading";
		}


		let existingResource = this.resources.get(resource.name);
		if (existingResource) {
			if (existingResource.path === resource.path) {
				return;
			}
			throw `Cant add resource "${resource.name}", another resource with the same name already exists!`;
			//existingResource.AddAlternativeResource(resource);
		}

		resource.AddOnLoadedCallback(() => this.ResourceLoaded(resource));
		resource.AddOnLoadErrorCallback(() => this.ResourceLoadError(resource)); 
		this.resources.set(resource.name, resource);
		resourcesThisStage.set(resource.name, resource);

		if (
			this.loadingStage == LoadingStage.GamesPreload ||
			this.loadingStage == LoadingStage.Games ||
			this.allowPostFinishResourceAdd)
		{
			if (!this.currentGame)
			{
				throw `Loading stage ${this.loadingStage} does not have set game`;
			}
			let resourcesThisGame = this.resourcesByGame.get(this.currentGame);
			resourcesThisGame.push(resource);
		}

		this.paths = [...this.paths, ...resource.GetPaths()];
	}


	public AddGlobal<T extends Resource>(resource: T) : T
	{
		let resourcesThisStage = this.resourcesByStage.get(this.loadingStage);

		//if (this.logging)
		{
			console.log(`    ResourceManager: stage ${this.loadingStage} Adding global resource ${resourcesThisStage.size +1} type ${resource.Type} name ${resource.name} from ${resource.path}`);
		}

		if (this.loadingInProgress && !this.onDemandLoadActive) {
            throw "Can't add resources after already started loading";
		}

		let existing: Resource = this.resources.get(resource.name);
		let casted: T = null; //existing as T;
		if (existing)
		{
			
			if (existing.constructor.name != resource.constructor.name)
			{
				throw `Adding global resource ${resource.name} of type ${resource.constructor.name}, but different type ${existing.constructor.name} already added.`;
			}

			if (existing.path != resource.path)
			{
				Log.Error(this.logGroup, "Global resource path is different from existing", {
					new: resource.path,
					existting: existing.path
				});
                
                throw `Global resource path is different from existing!\n    name: ${resource.name}\n     new: ${resource.path}\nexisting: ${existing.path}\n`;
			}

			existing.AddAlternativeResource(resource);

			casted = existing as T;
		}
		else
		{
			resource.AddOnLoadedCallback(() => this.ResourceLoaded(resource));
			resource.AddOnLoadErrorCallback(() => this.ResourceLoadError(resource)); 

			this.resources.set(resource.name, resource);
			casted = resource;

			resourcesThisStage.set(resource.name, resource);

			if (
				this.loadingStage == LoadingStage.GamesPreload ||
				this.loadingStage == LoadingStage.Games ||
				this.allowPostFinishResourceAdd)
			{
				if (!this.currentGame)
				{
					throw `Loading stage ${this.loadingStage} does not have set game`;
				}
				let resourcesThisGame = this.resourcesByGame.get(this.currentGame);
				resourcesThisGame.push(resource);
			}

			// let paths: string[] = resource.GetPaths();
			// for (let i = 0; i < paths.length; i++)
			// {
			// 	this.paths.push(paths[i]);
			// }
			this.paths = [...this.paths, ...resource.GetPaths()];
		}



		return casted;
	}


	allowGet: boolean = false;
	public async AllowGet(cb: () => Promise<void>)
	{
		this.allowGet = true;
		await cb();
		this.allowGet = false;
	}


	public Get<T extends Resource>(resourceName: string | ResourceIdentifier): T
	{
		if (!this.allowGet)
		{
			throw `ResourceManager: Can't get resource ${resourceName}, not allowed`;
		}

        let tmpResourceName: string = null;
        if (typeof resourceName == "string")
		{
			tmpResourceName = resourceName;
		}
		else if (resourceName instanceof ResourceIdentifier)
		{
			tmpResourceName = resourceName.ID();
		}
        else
        {
            throw `Resource name is of unknown type, allowed string or ResourceIdentifier`;
        }

		if (this.logging)
		{
			//console.log(`ResourceManager: Getting resource name ${tmpResourceName}`);
		}

		const resource: Resource = this.resources.get(tmpResourceName);;

		if (!resource)
        {
            throw `Trying to access resource ${tmpResourceName}, that does not exist`;
		}

		if (!resource.loaded)
		{
			throw `Trying to access resource ${tmpResourceName}, that is not loaded`;
		}

		// TODO downcast type check
		const result: T = resource as T;
		return result;
	}

	// mark as ready to load
	protected readyToLoad: boolean = false;
	public ReadyToLoad(): void
	{
		this.readyToLoad = true;
		this.readyToLoadCallbacks.forEach((cb) => cb());
	}

	protected readyToLoadCallbacks: ReadyToLoadCallback[] = [];
	public AddReadyToLoadCallback(callback: ReadyToLoadCallback): void
	{
	 	this.readyToLoadCallbacks.push(callback);
	}

	protected readonly batchSize: number = 200;

	//protected processedBeforeStart: number = 0;
	protected currentBatchIndex: number = -1;
	protected currentBatchProcessed: number = 0;


	protected loadPromise: Promise<void> = null;
	protected loadPromiseResolve: () => void = null;
	protected loadPromiseReject: () => void = null;

	public async Load(): Promise<void>
	{

		//console.log(`ResourceManager Loading stage ${this.loadingStage} resources ${this.resourcesByStage.get(this.loadingStage).size}`);


		//this.processedBeforeStart = this.resourcesProcessed;
		this.currentBatchIndex = 0;


		let resourcesThisStage = this.resourcesByStage.get(this.loadingStage);

		let resourceNamesThisStage = [...resourcesThisStage.keys()];
		for (let i = 0; i < resourceNamesThisStage.length; i++)
		{
			let resourceName = resourceNamesThisStage[i];
			let resource = resourcesThisStage.get(resourceName);

			//console.log(`    ResourceManager Loading resource ${this.processedResources.size + i}/${this.resources.size} stage ${this.loadingStage} resource ${resource.name}`);	
		}

		this.loadPromise = new Promise<void>((resolve, reject) => {
			this.loadPromiseResolve = resolve;
			this.loadPromiseReject = reject;
		});

		//this.loadPromise.then(() => console.log("Load Promise Resolved"));

		this.loadingInProgress = true;


		// if (this.loadTimeout > 0) {
		// 	this.loadTimeoutTimer = window.setTimeout(() => {
		// 		if (this.resourcesProcessed != this.resources.size) {
		// 			this.resources.forEach((resource: Resource, resourceName: string) => {
		// 				if (!resource.processed) {
		// 					Log.Error(this.logGroup, "Resource " + resourceName + " still not processed!");
		// 				}
		// 			});
		// 		}
		// 	}, this.loadTimeout);
		// }

		// find duplicate resource loading
		if (false)
		{
			let logGroup = this.logGroup.Add("Duplicates");

			// Log.Debug(logGroup, "Checking for duplicate resources");

			let res: Resource[] = [...this.resources.values()];

			let numDuplicates: number = 0;
			for (let i = 0; i < res.length -1; i++)
			{
				for (let j = i +1; j < res.length; j++)
				{
					if (res[i].path == res[j].path)
					{
						numDuplicates++;
						Log.Error(logGroup, "Duplicate resource " + res[j].name + " to " + res[j].name + " (" + res[i].path + ")");
					}
				}
			}

			if (numDuplicates > 0)
			{
				Log.Error(logGroup, "Total duplicates: " + numDuplicates);
			}
			else
			{
				// Log.Debug(logGroup, "No duplicates");
			}
		}

		// print resources paths that will get loaded
		// for (let i = 0; i < this.paths.length; i++)
		// {
		// 	console.log("LOAD: " + this.paths[i]);
		// }
		this.loadStartCallbacks.forEach((handler) => handler([...this.resources.values()]));

		// this.resources.forEach((resource: Resource, resourceName: string) => {
		// 	resource.Load(this.preventCache);
		// });


		

		// no resources, no loading
		if (this.processedResources.size == this.resources.size) {
			
			this.StageLoadFinished(this.loadingStage);

			// this was last stage loaded, we can finish loading
			if (!this.onDemandLoadActive && LoadingStageOrder.indexOf(this.loadingStage) == LoadingStageOrder.length -1)
			{
				this.LoadFinished();
			}

			return this.loadPromise;
		}

		// wait some time before loading
		setTimeout( () => this.LoadNextBatch(), 10);

		return this.loadPromise;
	}

	public async LoadStage(loadingStage: LoadingStage)
	{
		return this.Load();
	}

	protected LoadNextBatch(): void
	{
		
		this.currentBatchProcessed = 0;


		let keys: string[] = [...this.resources.keys()];

		if (keys.length == 0)
		{
			Log.Error(this.logGroup, "Nothing to load in batch " + this.currentBatchIndex);
		}

		let startIndex: number = this.processedResources.size;
		let endIndex: number = Math.min(this.processedResources.size + this.batchSize, keys.length);
		
		let batchKeys: string[] = keys.slice(startIndex, endIndex);

		for (let i = 0; i < batchKeys.length; i++)
		{
			//console.log(`        LoadNextBatch() batch: ${this.currentBatchIndex}, resource: ${batchKeys[i]}`);
			this.resources.get(batchKeys[i]).Load(this.preventCache);
		}

		this.currentBatchIndex++;
	}

	public Resources(): Map<string, Resource> {
		return this.resources;
	}

	private ResourceLoaded(resource: Resource): void
	{
		//console.log(`    ResourceManager Resource ${this.processedResources.size +1}/${this.resources.size} loaded ${this.loadingStage} resource ${resource.name}`);
		this.resourcesLoaded++;
		this.ResourceProcessed(resource, true);
	}

	private ResourceLoadError(resource: Resource): void
	{
		if (resource.loadRetryCount < 3)
		{
			resource.loadRetryCount++;

			Log.Important(this.logGroup, "Error loading resource " + resource.name + " from " + resource.path + ", retrying x" + resource.loadRetryCount + "");
			resource.Reload(this.preventCache);
		}
		else
		{
			//console.error(`    ResourceManager Resource ${this.processedResources.size +1}/${this.resources.size} error ${this.loadingStage} resource ${resource.name}`);

			this.resourcesLoadError++;

			if (resource.required) {
				Log.Error(this.logGroup, "Error loading resource " + resource.name + " from " + resource.path);
				this.failedResources.push(resource);
			}

			this.ResourceProcessed(resource, false);
		}
	}


	processedResources: Map<string, Resource> = new Map<string, Resource>();

	previousPercent: number = 0;
	private ResourceProcessed(resource: Resource, success: boolean): void
	{
		//this.resourcesProcessed++;
		this.currentBatchProcessed++;

		//console.log(`    ResourceManager Resource ${this.processedResources.size}/${this.resources.size} processed ${this.loadingStage} resource ${resource.name}`);

		// if (this.processedResources.has(resource.name))
		// {
		// 	throw `Resource ${resource.name} already processed, it is reporting processed multiple times`;
		// }

		this.processedResources.set(resource.name, resource);

		//let stagePercent = (LoadingStageOrder.indexOf(this.loadingStage) +1) / LoadingStageOrder.length;
		//let percent = Math.round(this.processedResources.size / this.resources.size * stagePercent * 100);

		//// Log.Debug(this.logGroup, "Progress: " + percent + "%, " + this.resourcesProcessed + "/" + this.resources.size + ": " + (success ? "SUCCESS" : "ERROR") + " " + resource.name + " from " + resource.path);

		this.LoadProgress(this.resources.size, this.processedResources.size);

		if (this.processedResources.size == this.resources.size)
		{
			this.StageLoadFinished(this.loadingStage);

			// this was last stage loaded, we can finish loading
			if (!this.onDemandLoadActive && LoadingStageOrder.indexOf(this.loadingStage) == LoadingStageOrder.length -1)
			{
				this.LoadFinished();
			}
		}
		else if (this.currentBatchProcessed == this.batchSize)
		{
			this.LoadNextBatch();
		}
	}

	public UnprocessedResources(): Resource[]
	{
		let unprocessed: Resource[] = [...this.resources.values()].filter(r => !r.processed);
		return unprocessed;
	}


	public EnsureAllLoaded(): boolean
	{
		let unprocessed = [...this.resources.values()].filter((resource: Resource) => !resource.processed);

		return unprocessed.length == 0;
	}


	// callbacks

	protected loadStartCallbacks: LoadStartCallback[] = [];
	public AddOnStart(handler: LoadStartCallback)
	{
		this.loadStartCallbacks.push(handler);
	}

	private loadProgressCallbacks: LoadProgressCallback[] = [];
	public AddLoadProgressCallback(callback: LoadProgressCallback): void {
		this.loadProgressCallbacks.push(callback);
	}

	private LoadProgress(_total: number, _loaded: number) {
		const stageCount = LoadingStageOrder.length;
		const currentStageIndex = LoadingStageOrder.indexOf(this.loadingStage);
		if (currentStageIndex < 0) {
			return;
		}

		const stageWeight = 100 / stageCount;
		// Completed stages are fully weighted; current stage adds partial progress.
		let percent = currentStageIndex * stageWeight;

		const stageResources = this.resourcesByStage.get(this.loadingStage);
		if (stageResources && stageResources.size > 0) {
			let stageLoaded = 0;
			for (const resource of stageResources.values()) {
				if (resource.processed) {
					stageLoaded++;
				}
			}
			percent += (stageLoaded / stageResources.size) * stageWeight;
		}

		percent = Math.min(100, Math.max(this.previousPercent, percent));
		this.previousPercent = percent;

		for (let i = 0; i < this.loadProgressCallbacks.length; i++) {
			this.loadProgressCallbacks[i](percent);
		}
	}

	private loadFinishedCallbacks: LoadFinishedCallback[] = [];
	public AddLoadFinishedCallback(callback: LoadFinishedCallback): void {
		this.loadFinishedCallbacks.push(callback);
	}

	private LoadFinished() {
		for (let i = 0; i < this.loadFinishedCallbacks.length; i++) {
			this.loadFinishedCallbacks[i]();
		}
	}


	private stageLoadFinishedCallbacks: StageLoadFinishedCallback[] = [];
	public AddStageLoadFinishedCallback(callback: StageLoadFinishedCallback): void {
		this.stageLoadFinishedCallbacks.push(callback);
	}

	private StageLoadFinished(stage: LoadingStage) {
		this.loadPromiseResolve?.();

		for (let i = 0; i < this.stageLoadFinishedCallbacks.length; i++) {
			this.stageLoadFinishedCallbacks[i](stage);
		}
	}

	public FailedResources(): Resource[] {
		return this.failedResources;
	}

	public GetResourcesCurrentStage(): Resource[] {
		return [...this.resourcesByStage.get(this.loadingStage).values()];
	}

	public GetResources(): Resource[]
	{
		return [...this.resources.values()];
	}
}
